//
// A significantly improved implementation of Observable's formatDate.js:
// https://github.com/observablehq/inspector/blob/e88c0459a71f9bcfd8c22b8c78262f71979821b2/src/formatDate.js
//
import { pad } from "./pad";

/**
 * Checks whether a date is UTC Midnight.
 */
export function isUTCMidnight(date: Date): boolean {
	return (
		date.getUTCMilliseconds() === 0 &&
		date.getUTCSeconds() === 0 &&
		date.getUTCMinutes() === 0 &&
		date.getUTCHours() === 0
	);
}

/**
 * Takes a date and returns a string representation of the date, formatted in the style of the
 * Chrome devtools JS console.
 */
export function formatDate(date: Date): string {
	// Takes a year as a number and returns a string representation of the year, formatted in the style
	// of the Chrome devtools JS console. Example input/output combinations:
	//
	//     1999  => "1999"
	//     300   => "0300"
	//     -1    => "-000001"
	//     10000 => "+010000"
	function formatYear(year: number): string {
		let formatted = pad(year, 4);

		if (year < 0) {
			formatted = "-" + pad(-year, 6);
		} else if (year > 9999) {
			formatted = "+" + pad(year, 6);
		}

		return formatted;
	}

	if (date.toString() === "Invalid Date" || Number.isNaN(date.getTime())) {
		return "Invalid Date";
	} else if (isUTCMidnight(date)) {
		const year = formatYear(date.getUTCFullYear());
		const month = pad(date.getUTCMonth() + 1, 2);
		const day = pad(date.getUTCDate(), 2);
		return `${year}-${month}-${day}`;
	} else {
		const year = formatYear(date.getFullYear());
		const month = pad(date.getMonth() + 1, 2);
		const day = pad(date.getDate(), 2);
		const hours = pad(date.getHours(), 2);
		const minutes = pad(date.getMinutes(), 2);
		const seconds = pad(date.getSeconds(), 2);
		const milliseconds = date.getMilliseconds();
		return milliseconds
			? `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${pad(milliseconds, 3)}`
			: `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
	}
}
